export function getPasswordValidationMessage(password, regex) {
    if (!password || !regex)
        return null;

    let passwordRegex;

    try {
        passwordRegex = new RegExp(regex);
    } catch (e) {
        return 'Password requirements are invalid.';
    }

    if (passwordRegex.test(password))
        return null;

    const missing = [];
    const invalid = [];

    /*
     * Lowercase
     */
    if (
        (
            regex.includes('(?=.*[a-z])') ||
            regex.includes('(?=.*[a-zA-Z])')
        ) &&
        !/[a-z]/.test(password)
    ) {
        missing.push('one lowercase letter');
    }

    /*
     * Uppercase
     */
    if (
        (
            regex.includes('(?=.*[A-Z])') ||
            regex.includes('(?=.*[a-zA-Z])')
        ) &&
        !/[A-Z]/.test(password)
    ) {
        missing.push('one uppercase letter');
    }

    /*
     * Number
     */
    if (
        (
            regex.includes('(?=.*\\d)') ||
            regex.includes('(?=.*[0-9])')
        ) &&
        !/\d/.test(password)
    ) {
        missing.push('one number');
    }

    /*
     * Positive lookaheads containing character classes.
     *
     * Examples:
     * (?=.*[@$!%*?&])
     * (?=.*[!@#])
     * (?=.*[^A-Za-z0-9])
     */
    const lookaheadRegex = /\(\?=\.\*\[([^\]]+)\]\)/g;

    let lookaheadMatch;

    while ((lookaheadMatch = lookaheadRegex.exec(regex)) !== null) {
        const characterClass = lookaheadMatch[1];

        // Already handled above
        if (
            characterClass === 'a-z' ||
            characterClass === 'A-Z' ||
            characterClass === 'a-zA-Z' ||
            characterClass === 'A-Za-z' ||
            characterClass === '0-9'
        ) {
            continue;
        }

        let requirementRegex;

        try {
            requirementRegex = new RegExp('[' + characterClass + ']');
        } catch (e) {
            continue;
        }

        if (!requirementRegex.test(password)) {
            /*
             * Generic special-character requirement:
             * (?=.*[^A-Za-z0-9])
             */
            if (
                characterClass === '^A-Za-z0-9' ||
                characterClass === '^a-zA-Z0-9'
            ) {
                missing.push('one special character');
            } else {
                /*
                 * Explicit allowed special characters:
                 * (?=.*[@$!%*?&])
                 */
                const displayCharacters = characterClass
                    .replace(/\\/g, '');

                missing.push(
                    'one special character (' +
                    displayCharacters +
                    ')'
                );
            }
        }
    }

    /*
     * Length
     *
     * Supports:
     *
     * {8}      exactly 8
     * {8,}     minimum 8
     * {8,20}   between 8 and 20
     */
    const lengthMatches = [...regex.matchAll(/\{(\d+)(?:,(\d*))?\}/g)];

    if (lengthMatches.length) {
        /*
         * Password regexes normally have the length quantifier on
         * the final character class / dot.
         *
         * Use the last quantifier found to avoid accidentally using
         * one from an earlier lookahead.
         */
        const lengthMatch = lengthMatches[lengthMatches.length - 1];

        const minLength = parseInt(lengthMatch[1], 10);

        const hasComma = lengthMatch[0].includes(',');

        const maxLength =
            hasComma && lengthMatch[2]
                ? parseInt(lengthMatch[2], 10)
                : hasComma
                    ? null
                    : minLength;

        if (minLength === maxLength) {
            if (password.length !== minLength)
                missing.unshift(
                    'exactly ' + minLength + ' characters'
                );
        } else {
            if (password.length < minLength) {
                missing.unshift(
                    'at least ' + minLength + ' characters'
                );
            } else if (
                maxLength !== null &&
                password.length > maxLength
            ) {
                missing.unshift(
                    'no more than ' + maxLength + ' characters'
                );
            }
        }
    }

    /*
     * Detect invalid characters from the final allowed
     * character class.
     *
     * Example:
     *
     * [A-Za-z\d@$!%*?&]{8,}
     *
     * means characters outside that class are not allowed.
     */
    const allowedClassMatch = regex.match(
        /\[([^\]]+)\]\{(?:\d+)(?:,\d*)?\}\$?$/
    );

    if (allowedClassMatch) {
        const allowedClass = allowedClassMatch[1];

        try {
            const invalidCharacterRegex =
                new RegExp('[^' + allowedClass + ']', 'g');

            const invalidCharacters =
                password.match(invalidCharacterRegex);

            if (invalidCharacters && invalidCharacters.length) {
                const uniqueInvalidCharacters =
                    [...new Set(invalidCharacters)];

                invalid.push(
                    'invalid character' +
                    (uniqueInvalidCharacters.length > 1 ? 's' : '') +
                    ' (' +
                    uniqueInvalidCharacters.join(' ') +
                    ')'
                );
            }
        } catch (e) {
            // Unsupported character class.
            // Fall through to the generic message if necessary.
        }
    }

    /*
     * Build message
     */
    const messages = [];

    if (missing.length) {
        messages.push(
            'Password must contain ' +
            missing.join(', ') +
            '.'
        );
    }

    if (invalid.length) {
        messages.push(
            'Password contains ' +
            invalid.join(', ') +
            '.'
        );
    }

    return messages.length
        ? messages.join(' ')
        : 'Password does not meet the password requirements.';
}